import { callExternalApi, CallResult } from '../callExternalApi';
import { blobWithFilename, NamedBlob } from '../../utils/fileDownload';
import {
  AdviseFeatureInput,
  AdviseFeatureResponse,
  FeatureInputImported,
  FeatureInputPreview
} from '../../types/advise';

const featureInputUrl = (engagementId: string) => `api/advise/engagements/${engagementId}/feature_input`;

export const fetchAdviseFeatureInput = async (engagementId: string): Promise<CallResult<AdviseFeatureInput>> => {
  return callExternalApi<AdviseFeatureInput>({
    url: featureInputUrl(engagementId),
    method: 'GET',
    headers: { 'content-type': 'application/json' }
  });
};

const blobText = (blob: Blob): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error);
    reader.readAsText(blob);
  });
};

/** The supplier's requirement sheet as an .xlsx, named by the API; a refusal's JSON body is read back out of the blob. */
export const fetchAdviseRequirementSheet = async (
  engagementId: string,
  supplierId: string
): Promise<CallResult<NamedBlob>> => {
  const result = await callExternalApi<NamedBlob>({
    url: `${featureInputUrl(engagementId)}/suppliers/${supplierId}/sheet`,
    method: 'GET',
    responseType: 'blob',
    transformResponse: blobWithFilename
  });
  const response = result.error?.errorResponse?.response;
  const refusal = (response?.data as NamedBlob | undefined)?.blob;
  if (response && refusal instanceof Blob) {
    try {
      response.data = JSON.parse(await blobText(refusal));
    } catch {
      // Not a refusal from the API, so the caller's fallback wording stands.
    }
  }
  return result;
};

const sendSheet = <T>(url: string, file: File, flagged?: string[]): Promise<CallResult<T>> => {
  const formData = new FormData();
  formData.append('file', file);
  flagged?.forEach((key) => formData.append('flagged[]', key));
  return callExternalApi<T>({
    url,
    method: 'POST',
    headers: { 'content-type': 'multipart/form-data' },
    data: formData
  });
};

export const previewAdviseSupplierResponse = async (
  engagementId: string,
  supplierId: string,
  file: File
): Promise<CallResult<FeatureInputPreview>> => {
  return sendSheet<FeatureInputPreview>(`${featureInputUrl(engagementId)}/suppliers/${supplierId}/preview`, file);
};

export const importAdviseSupplierResponse = async (
  engagementId: string,
  supplierId: string,
  file: File,
  flagged: string[]
): Promise<CallResult<FeatureInputImported>> => {
  return sendSheet<FeatureInputImported>(
    `${featureInputUrl(engagementId)}/suppliers/${supplierId}/import`,
    file,
    flagged
  );
};

export const updateAdviseFeatureResponseFlag = async (
  engagementId: string,
  responseId: string,
  flagged: boolean
): Promise<CallResult<AdviseFeatureResponse>> => {
  return callExternalApi<AdviseFeatureResponse>({
    url: `${featureInputUrl(engagementId)}/responses/${responseId}/flag`,
    method: 'PUT',
    headers: { 'content-type': 'application/json' },
    data: { flagged }
  });
};
